import type { RefObject } from 'react'
import type { AggregatedOrderView } from '@/types/api/aggregated/order'
import type {
  AllocationUpdateEventMessage,
  TradingEventMessage,
} from '@/types/api/events/trading-event'

import { cloneDeep } from 'lodash'

import { createLogger } from '@/lib/logger'

import { updateLatestMsgNumber, upsertOrder } from './helpers'

const LOGGER = createLogger(
  'utils/trade-blotter/trading-events/allocation-update',
)

function constructUpdatedOrderView(
  allocationUpdateEventMessage: AllocationUpdateEventMessage,
  existingOrderView: AggregatedOrderView | null,
): AggregatedOrderView {
  const { event: allocationUpdateEvent, summary: allocationUpdateSummary } =
    allocationUpdateEventMessage
  const existingTradingEvents = existingOrderView?.trading_events || []
  // Want to prevent duplicate trading events from appearing in the blotter
  // in the event we receive a duplicate message because we reconnected to
  // the websocket at a high water mark we have already processed.
  const filteredTradingEvents = existingTradingEvents.filter(
    (event: TradingEventMessage) =>
      event.msg_number !== allocationUpdateEventMessage.msg_number,
  )
  return updateLatestMsgNumber(
    {
      ...(existingOrderView || {}),
      id: existingOrderView?.id || allocationUpdateSummary.order_id,
      allocation_status: allocationUpdateSummary.status,
      account_requests: allocationUpdateSummary.account_requests,
      allocation_info: allocationUpdateSummary.account_allocations,
      // TODO: We will need to refactor this when we can do multi-custodial trades
      custodian_id: allocationUpdateSummary.custodian_id,
      last_allocation_update_event: allocationUpdateEvent,
      last_order_update_event:
        existingOrderView?.last_order_update_event || null,
      last_system_update_event:
        existingOrderView?.last_system_update_event || null,
      // Cloning since we are concatenating to the array and AG Grid needs a new reference to detect changes
      trading_events: cloneDeep(
        filteredTradingEvents.concat(allocationUpdateEventMessage),
      ),
      rfq_views: existingOrderView?.rfq_views || null,
      reject_reason: existingOrderView?.reject_reason || null,
      cancel_reason: existingOrderView?.cancel_reason || null,
    },
    allocationUpdateEventMessage.msg_number,
  )
}

/**
 * Processes allocation updates received via HTTP and updates the ordersMap.
 * Only called as part of the transformResponse callback in the TradingSlice.
 * @param AggregatedOrderViewsMap Map of existing AggregatedOrderView objects
 * @param tradingEvent Incoming trading event
 */
export const processAllocationUpdate = (
  AggregatedOrderViewsMap: Map<string, AggregatedOrderView>,
  tradingEvent: TradingEventMessage,
): void => {
  const allocationUpdateEvent = tradingEvent as AllocationUpdateEventMessage
  const orderId = allocationUpdateEvent.summary.order_id
  const existingOrderView = AggregatedOrderViewsMap.get(orderId) || null
  const updatedOrderView = constructUpdatedOrderView(
    allocationUpdateEvent,
    existingOrderView,
  )
  AggregatedOrderViewsMap.set(orderId, updatedOrderView)
}

/**
 * Handles allocation updates received via WebSocket and updates the data cached in Redux.
 * @param tradingEvent Incoming trading event
 * @param updateCachedData Function to update the cached data in Redux
 */
export const handleAllocationUpdate = (
  tradingEvent: TradingEventMessage,
  aggregatedOrderViewsRef: RefObject<Map<string, AggregatedOrderView>>,
  onUpdateApplied: ({
    toAdd,
    toUpdate,
    toRemove,
  }: {
    toAdd?: AggregatedOrderView[]
    toUpdate?: AggregatedOrderView[]
    toRemove?: AggregatedOrderView[]
  }) => void,
) => {
  const allocationUpdateEventMessage =
    tradingEvent as AllocationUpdateEventMessage

  LOGGER.info('Allocation WebSocket event received', {
    browserTime: new Date().toISOString(),
    eventId: allocationUpdateEventMessage.event_id,
    eventTime: allocationUpdateEventMessage.event.time,
    eventType: allocationUpdateEventMessage.event.event_type,
    msgNumber: allocationUpdateEventMessage.msg_number,
    orderId: allocationUpdateEventMessage.summary.order_id,
  })

  const orderId = allocationUpdateEventMessage.summary.order_id

  const map = aggregatedOrderViewsRef.current
  const existingOrderView = map.get(orderId) || null
  const updatedOrderView = constructUpdatedOrderView(
    allocationUpdateEventMessage,
    existingOrderView,
  )
  upsertOrder(map, orderId, updatedOrderView, onUpdateApplied)
  if (existingOrderView === null) {
    LOGGER.debug(
      'Allocation update received for order but no order found in the map',
      { orderId },
    )
  }
}
